import Warning from '@mui/icons-material/Warning';
import { Box, Button, Typography } from '@mui/material';
import { ETranscoderProfileKind, TranscoderProfile } from '@vxui/common';
import { Modal } from '@vxui/components';
import { FC, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

export type BulkDeleteProfileModalConfig = {
  open: boolean;
  title?: string;
  description?: string;
  saveBtn?: ReactElement;
};

type ModalProps = {
  kind: ETranscoderProfileKind;
  selectedProfiles: TranscoderProfile[];
  onClose: () => void;
};

type ActionsProps = {
  profilesToRemove: TranscoderProfile[];
  saveBtn?: ReactElement;
  onClick: () => void;
};

const Actions: FC<ActionsProps> = ({ profilesToRemove, saveBtn, onClick }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box>
      <Button
        size="medium"
        variant="outlined"
        color="secondary"
        sx={{ marginRight: profilesToRemove.length > 0 ? '15px' : '0' }}
        onClick={onClick}
      >
        {t(`button.${profilesToRemove.length > 0 ? 'cancel' : 'close'}`)}
      </Button>
      {profilesToRemove.length > 0 && saveBtn}
    </Box>
  );
};

export const BulkDeleteProfileModal: FC<BulkDeleteProfileModalConfig & ModalProps> = ({
  open,
  saveBtn,
  kind,
  selectedProfiles,
  onClose,
}) => {
  const { t } = useTranslation();
  const profilesToRemove = selectedProfiles.filter(profile => profile.templates?.length === 0);
  const blockedProfiles = selectedProfiles.filter(profile => profile.templates?.length !== 0);
  const hasProfilesToRemove = profilesToRemove.length > 0;
  const hasBlockedProfiles = blockedProfiles.length > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t(`transcoder.modal.delete.${kind}Profiles.title`)}
      actions={<Actions profilesToRemove={profilesToRemove} onClick={onClose} saveBtn={saveBtn} />}
      maxWidth="sm"
    >
      <Box display="flex" flexDirection="column" rowGap="15px">
        {hasProfilesToRemove && (
          <Box display="flex" flexDirection="column" rowGap="5px">
            <Typography variant="p16" component="span">
              {t(`transcoder.modal.delete.${kind}Profiles.description`)}
            </Typography>
            {profilesToRemove.map((template: TranscoderProfile) => (
              <Typography key={`profile-to-remove-${template.id}`} variant="p16Bold" marginLeft="40px">
                {template.name}
              </Typography>
            ))}
          </Box>
        )}
        {hasBlockedProfiles && (
          <Box display="flex" flexDirection="row" alignItems="flex-start" gap="16px">
            <Warning color="warning" fontSize="medium" />
            <Box display="flex" flexDirection="column" rowGap="5px">
              <Typography variant="p16Medium" color="warning">
                {t(`transcoder.modal.delete.${kind}Profiles.descriptionInUse`)}
              </Typography>
              {blockedProfiles.map((profile: TranscoderProfile) => (
                <Typography key={`blocked-profile-${profile.id}`} variant="p16Bold" color="warning">
                  {profile.name}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Modal>
  );
};
